import { PushNotifications } from '@capacitor/push-notifications';
import type { PushNotificationService } from '../types/push-notification-service.interface';
import type { NotificationPayload } from '../types/notification-types';

export class CapacitorPushService implements PushNotificationService {
  private tokenRefreshCallbacks: ((token: string) => void)[] = [];
  private messageCallbacks: ((message: any) => void)[] = [];
  private actionCallbacks: ((payload: NotificationPayload) => void)[] = [];
  private currentToken: string | null = null;

  async initialize(): Promise<void> {
    console.log('[CapacitorPush] 🚀 Initializing Capacitor Push Service...');

    // Only setup listeners on initialize
    // Don't request permissions automatically - let the app control when to ask
    this.setupListeners();

    // Check current permissions (but don't request)
    const permStatus = await PushNotifications.checkPermissions();
    console.log('[CapacitorPush] 📋 Current permission status:', permStatus);

    // If already granted, register automatically
    if (permStatus.receive === 'granted') {
      console.log('[CapacitorPush] ✅ Permission already granted, registering...');
      try {
        await PushNotifications.register();
        console.log('[CapacitorPush] ✅ Registration initiated successfully');
      } catch (error) {
        console.error('[CapacitorPush] ❌ Registration failed:', error);
      }
    } else {
      console.log('[CapacitorPush] ⏳ Permission not granted yet. Call requestPermission() when ready.');
    }
  }

  private setupListeners(): void {
    console.log('[CapacitorPush] 👂 Setting up listeners...');

    // Listen for registration success
    PushNotifications.addListener('registration', (token) => {
      console.log('[CapacitorPush] 🎉 Registration success! Token:', token.value);
      this.currentToken = token.value;
      this.tokenRefreshCallbacks.forEach((cb) => cb(token.value));
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[CapacitorPush] ❌ Registration error:', error);
    });

    // Listen for push notifications
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[CapacitorPush] 📩 Push notification received:', notification);
      this.messageCallbacks.forEach((cb) => cb(notification));
    });

    // Listen for notification action performed (tap)
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('[CapacitorPush] 👆 Notification tapped:', notification);
      const data = notification.notification.data;

      // Backend sends: { type: "ONLINE_ORDER", entityId: "uuid" }
      if (data?.type && data?.entityId) {
        const payload: NotificationPayload = {
          type: data.type,
          entityId: data.entityId,
        };
        this.actionCallbacks.forEach((cb) => cb(payload));
      } else {
        console.warn('[CapacitorPush] Notification missing type or entityId:', data);
      }
    });
  }

  async requestPermission(): Promise<boolean> {
    console.log('[CapacitorPush] 🔔 Requesting push notification permissions...');
    const result = await PushNotifications.requestPermissions();
    console.log('[CapacitorPush] 📝 Permission request result:', result);

    const granted = result.receive === 'granted';

    if (granted) {
      console.log('[CapacitorPush] ✅ Permission granted! Registering for push...');
      try {
        await PushNotifications.register();
        console.log('[CapacitorPush] ✅ Registration initiated successfully');
      } catch (error) {
        console.error('[CapacitorPush] ❌ Registration failed:', error);
      }
    } else {
      console.warn('[CapacitorPush] ❌ Permission denied or dismissed');
    }

    return granted;
  }

  async getToken(): Promise<string | null> {
    return this.currentToken;
  }

  onTokenRefresh(callback: (token: string) => void): void {
    this.tokenRefreshCallbacks.push(callback);
  }

  onMessage(callback: (message: any) => void): void {
    this.messageCallbacks.push(callback);
  }

  onAction(callback: (payload: NotificationPayload) => void): void {
    this.actionCallbacks.push(callback);
  }

  async cleanup(): Promise<void> {
    await PushNotifications.removeAllListeners();
    this.tokenRefreshCallbacks = [];
    this.messageCallbacks = [];
    this.actionCallbacks = [];
    this.currentToken = null;
  }
}
